import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Skeleton } from '@/components/ui/skeleton'
import { useServiceWithAddonConfig } from '@/modules/Service/services'
import { ServiceInformationProps, ServiceStatus } from '@/modules/Service/types'
import {
    transformServiceDetailData,
    getRowClassName,
    useServiceDetailActions,
} from '@/modules/Service/utils/service-detail'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertError } from 'vnetwork-ui'
import { createHostingColumns } from './hosting-config'
import ServiceTechnical from '../../../shared/ServiceTechnical'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { data, isLoading, isError, error, isPending } =
        useServiceWithAddonConfig(service?.service_id)
    const { t } = useTranslation(['services', 'customer'])

    // Transform data using reusable utility
    const rows = useMemo(
        () =>
            transformServiceDetailData({
                service,
                data,
                defaultTemplate: 'Cloud.Hosting',
            }),
        [data, service]
    )

    const columns = useMemo(() => createHostingColumns(t, rows), [t, rows])
    const actions = useServiceDetailActions(t)

    // Hidden columns based on service status and data availability
    const hiddenColumns = useMemo(() => {
        const hidden = new Set<string>()
        if (service?.service_status !== ServiceStatus.Cancelled) {
            hidden.add('service_stopped_date')
        }
        const hideRenewStatuses = [
            ServiceStatus.PendingTerminated,
            ServiceStatus.Cancelled,
        ]
        if (
            hideRenewStatuses.includes(
                service?.service_status as ServiceStatus
            ) ||
            !service?.continue_startdate
        ) {
            hidden.add('service_renew_cycling')
        }
        return hidden
    }, [service?.service_status, service?.continue_startdate])

    if (isError) return <AlertError error={error} />
    if (isLoading || isPending) {
        return (
            <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        )
    }

    return (
        <>
            <DataTableCustomized
                columns={columns}
                rows={rows}
                rowKey={(row) => row._rowId}
                rowClassName={getRowClassName}
                size="small"
                toolbar={{ hiddenColumns }}
                actions={actions}
            />
            <ServiceTechnical service={service} />
        </>
    )
}

export default Information
